import { FunctionComponent, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../Button/Button";
import { ButtonColor, ButtonSize } from "../Button/types";

interface TableEmptyRowProps {
	/** Number of columns to span (the empty state is one full-width cell). */
	colSpan: number;
	/** Custom empty-state node; falls back to the generic "no data" message. */
	noDataText?: ReactNode;
	/** Optional primary action shown below the empty-state content. */
	noDataCta?: { title: string; action: () => void };
	/** Visible width of the scroll viewport, so the empty state stays centered in view on wide tables. */
	viewportWidth?: number;
}

/**
 * The single full-width row a {@link Table} renders when it has no rows.
 *
 * The cell fills the table's height (the parent `<table>` is stretched to the viewport in sticky
 * mode) so the empty state stays vertically centered *in view* instead of pinned to the top with a
 * gap beneath it, and is pinned to the visible scroll viewport horizontally via `viewportWidth`
 * (so it stays centered on very wide, horizontally scrollable tables). The `min-h-[496px]` keeps a
 * comfortable floor for short content / non-sticky tables.
 */
export const TableEmptyRow: FunctionComponent<TableEmptyRowProps> = ({
	colSpan,
	noDataText,
	noDataCta,
	viewportWidth,
}) => {
	const { t } = useTranslation();

	return (
		<tr className="text-gray-700 text-sm">
			<td className="h-full p-0 bg-white border-t border-border-light" colSpan={colSpan}>
				<div
					className="sticky left-0 flex h-full min-h-[496px] flex-col items-center justify-center px-4 text-center"
					style={viewportWidth ? { width: viewportWidth } : undefined}
				>
					{noDataText ? noDataText : t("basics.noData")}
					{noDataCta && (
						<div className="w-auto mt-4">
							<Button
								color={ButtonColor.ACTION}
								size={ButtonSize.S}
								title={noDataCta.title}
								onClick={noDataCta.action}
							/>
						</div>
					)}
				</div>
			</td>
		</tr>
	);
};
